import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";
import { downloadObjectsCsv } from "../../../helpers/csvExport";

const BASE_URL = API_BASE;

/**
 * DURUM alanı veritabanında Türkçe karakter olmadan yazılmış ("Incelendi"),
 * panel ise "İncelendi" bekliyordu — bu yüzden mevcut kayıtların rozetleri
 * "Bilinmiyor" görünüyor, butonlar yanlış durumu gösteriyordu.
 * Karşılaştırmalar normalize edilmiş anahtar üzerinden yapılır.
 */
const durumKey = (durum) => {
  const d = (durum || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i");
  if (d === "beklemede" || d === "yeni") return "beklemede";
  if (d === "incelendi" || d === "inceleniyor") return "incelendi";
  if (d === "reddedildi") return "reddedildi";
  return d;
};

const SikayetYonetimi = () => {
  document.title = "Şikayet Yönetimi | HarmanaGEL";

  const [sikayetler, setSikayetler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [savingDurum, setSavingDurum] = useState(false);

  const fetchSikayetler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url =
        `${BASE_URL}/ILAN_SIKAYETLERI` +
        `?$top=2000` +
        `&$expand=KULLANICILAR($select=ID,AD_SOYAD,Mail_Adress,CEP_NO),ILANLAR($select=ID,AD,GORSEL_YOLU,KIMDEN_ID)` +
        `&$orderby=TARIH desc`;
      const res = await fetch(url, { cache: "no-store", headers: getAuthHeaders() });
      const result = await res.json();
      if (result && result.value) setSikayetler(result.value);
    } catch (err) {
      setError("Şikayetler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchSikayetler(); }, []);

  const setSuccessTemp = (m) => {
    setSuccessMsg(m);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleView = (s) => {
    setSelected(s);
    setModal(true);
  };

  const updateDurum = async (yeniDurum) => {
    if (!selected) return;
    setSavingDurum(true);
    try {
      const res = await fetch(`${BASE_URL}/ILAN_SIKAYETLERI(${selected.ID})`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ DURUM: yeniDurum }),
      });
      if (!res.ok) throw new Error("Durum güncellenemedi.");
      setSikayetler((prev) =>
        prev.map((x) => (x.ID === selected.ID ? { ...x, DURUM: yeniDurum } : x))
      );
      setSelected({ ...selected, DURUM: yeniDurum });
      setSuccessTemp(`Şikayet ${yeniDurum} olarak işaretlendi.`);
    } catch (e) {
      setError(e.message || "İşlem başarısız.");
    } finally {
      setSavingDurum(false);
    }
  };

  const passifYapIlan = async () => {
    if (!selected?.ILAN_ID) return;
    if (!window.confirm("Şikayet edilen ilanı pasif duruma getirmek istiyor musunuz?")) return;
    setSavingDurum(true);
    try {
      const res = await fetch(`${BASE_URL}/ILANLAR(${selected.ILAN_ID})`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ AKTIF_DURUM: "Pasif" }),
      });
      if (!res.ok) throw new Error("İlan pasifleştirilemedi.");
      await updateDurum("İncelendi");
      setSuccessTemp("İlan pasif duruma alındı.");
    } catch (e) {
      setError(e.message || "İşlem başarısız.");
    } finally {
      setSavingDurum(false);
    }
  };

  const getDurumBadge = (durum) => {
    const map = {
      beklemede: { color: "warning", label: "Beklemede" },
      incelendi: { color: "success", label: "İncelendi" },
      reddedildi: { color: "danger", label: "Reddedildi" },
    };
    const cfg = map[durumKey(durum)] || { color: "secondary", label: durum || "-" };
    return <Badge color={cfg.color}>{cfg.label}</Badge>;
  };

  const bekleyenCount = useMemo(
    () => sikayetler.filter((s) => durumKey(s.DURUM) === "beklemede").length,
    [sikayetler]
  );

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Şikayet Eden",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const u = cell.row.original.KULLANICILAR;
        if (!u) return <span className="text-muted">Bilinmiyor</span>;
        return (
          <div>
            <div className="fw-medium">{u.AD_SOYAD || "-"}</div>
            <small className="text-muted">{u.Mail_Adress || u.CEP_NO || ""}</small>
          </div>
        );
      },
    },
    {
      header: "İlan",
      accessorKey: "ILANLAR",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const i = cell.row.original.ILANLAR;
        if (!i) return <span className="text-muted">İlan silinmiş #{cell.row.original.ILAN_ID}</span>;
        return (
          <div className="d-flex align-items-center gap-2">
            {i.GORSEL_YOLU && (
              <img
                src={i.GORSEL_YOLU}
                alt={i.AD}
                style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover" }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <div className="fw-medium" style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {i.AD || `İlan #${i.ID}`}
              </div>
              <small className="text-muted">#{i.ID}</small>
            </div>
          </div>
        );
      },
    },
    {
      header: "Şikayet Tipi",
      accessorKey: "SIKAYET_TIP",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => <Badge color="info" className="bg-soft-info text-info">{cell.getValue() || "-"}</Badge>,
    },
    {
      header: "Açıklama",
      accessorKey: "ACIKLAMA",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <span
          style={{ maxWidth: 220, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={cell.getValue()}
        >
          {cell.getValue() || "-"}
        </span>
      ),
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => getDurumBadge(cell.getValue()),
    },
    {
      header: "Tarih",
      accessorKey: "TARIH",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const s = cellProps.row.original;
        return (
          <Button
            color={durumKey(s.DURUM) === "beklemede" ? "danger" : "primary"}
            size="sm"
            outline={durumKey(s.DURUM) !== "beklemede"}
            onClick={() => handleView(s)}
          >
            <i className="bx bx-show me-1" />İncele
          </Button>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Şikayet Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {bekleyenCount > 0 && (
            <Alert color="warning" className="d-flex align-items-center gap-2">
              <i className="bx bx-error fs-4" />
              <span><strong>{bekleyenCount} bekleyen şikayet</strong> incelemenizi bekliyor.</span>
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">
                      İlan Şikayetleri
                      {bekleyenCount > 0 && <Badge color="warning" pill className="ms-2">{bekleyenCount}</Badge>}
                    </h4>
                    <div className="d-flex gap-2">
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={() =>
                          downloadObjectsCsv(
                            `sikayetler-${new Date().toISOString().slice(0, 10)}.csv`,
                            sikayetler,
                            [
                              { key: "ID", label: "ID" },
                              {
                                label: "Şikayet Eden",
                                get: (s) => s.KULLANICILAR?.AD_SOYAD || "",
                              },
                              {
                                label: "İlan",
                                get: (s) => s.ILANLAR?.AD || s.ILAN_ID,
                              },
                              { key: "SIKAYET_TIP", label: "Tip" },
                              { key: "DURUM", label: "Durum" },
                              {
                                label: "Tarih",
                                get: (s) =>
                                  s.TARIH ? new Date(s.TARIH).toLocaleString("tr-TR") : "",
                              },
                            ]
                          )
                        }
                        disabled={!sikayetler.length}
                      >
                        <i className="bx bx-export me-1" />CSV
                      </Button>
                      <Button color="primary" size="sm" onClick={fetchSikayetler} disabled={isLoading}>
                        <i className="bx bx-refresh me-1" />Yenile
                      </Button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={sikayetler}
                      isGlobalFilter={true}
                      SearchPlaceholder="Şikayet ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setModal(false)}>
          Şikayet #{selected?.ID} {selected && getDurumBadge(selected.DURUM)}
        </ModalHeader>
        <ModalBody>
          {selected && (
            <>
              <div className="mb-3 p-3 bg-light rounded">
                <Row className="g-3">
                  <Col md={6}>
                    <small className="text-muted d-block">Şikayet Eden</small>
                    <strong>{selected.KULLANICILAR?.AD_SOYAD || "Bilinmiyor"}</strong>
                    {selected.KULLANICILAR?.Mail_Adress && (
                      <div><small className="text-muted">{selected.KULLANICILAR.Mail_Adress}</small></div>
                    )}
                    {selected.KULLANICILAR?.CEP_NO && (
                      <div><small className="text-muted">{selected.KULLANICILAR.CEP_NO}</small></div>
                    )}
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Tarih</small>
                    <strong>{selected.TARIH ? new Date(selected.TARIH).toLocaleString("tr-TR") : "-"}</strong>
                    <div><small className="text-muted">Şikayet Tipi: {selected.SIKAYET_TIP || "-"}</small></div>
                  </Col>
                </Row>
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">Açıklama</label>
                <Input
                  type="textarea"
                  rows={3}
                  value={selected.ACIKLAMA || ""}
                  readOnly
                  style={{ background: "#f8f9fa" }}
                />
              </div>

              {selected.ILANLAR && (
                <div className="p-3 border rounded mb-3">
                  <div className="d-flex align-items-center gap-3">
                    {selected.ILANLAR.GORSEL_YOLU && (
                      <img
                        src={selected.ILANLAR.GORSEL_YOLU}
                        alt={selected.ILANLAR.AD}
                        style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover" }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <small className="text-muted d-block">Şikayet Edilen İlan</small>
                      <strong>{selected.ILANLAR.AD}</strong>
                      <div><small className="text-muted">İlan ID: #{selected.ILANLAR.ID}</small></div>
                      <div><small className="text-muted">Sahibi ID: #{selected.ILANLAR.KIMDEN_ID}</small></div>
                    </div>
                  </div>
                </div>
              )}

              {!selected.ILANLAR && selected.ILAN_ID && (
                <Alert color="warning">
                  <i className="bx bx-error me-1" />
                  Bu ilan artık erişilebilir değil (silinmiş veya pasif). İlan ID: #{selected.ILAN_ID}
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter className="d-flex flex-wrap gap-2">
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={savingDurum}>
            Kapat
          </Button>
          {durumKey(selected?.DURUM) !== "reddedildi" && (
            <Button color="warning" outline onClick={() => updateDurum("Reddedildi")} disabled={savingDurum}>
              <i className="bx bx-x me-1" />Reddet
            </Button>
          )}
          {durumKey(selected?.DURUM) !== "incelendi" && (
            <Button color="success" outline onClick={() => updateDurum("İncelendi")} disabled={savingDurum}>
              <i className="bx bx-check me-1" />İncelendi İşaretle
            </Button>
          )}
          {selected?.ILAN_ID && selected?.ILANLAR && (
            <Button color="danger" onClick={passifYapIlan} disabled={savingDurum}>
              {savingDurum ? <Spinner size="sm" /> : <><i className="bx bx-trash me-1" />İlanı Pasif Yap</>}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SikayetYonetimi;
